import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { money, catLabel, asset, timeLeft } from "@/lib/api";
import { motion } from "framer-motion";
import { Gavel, Clock, Package, Layers, Store, Star } from "lucide-react";

export const AuctionCard = ({ auction, index = 0 }) => {
  const [t, setT] = useState(timeLeft(auction.ends_at));
  useEffect(() => {
    const id = setInterval(() => setT(timeLeft(auction.ends_at)), 1000);
    return () => clearInterval(id);
  }, [auction.ends_at]);

  const isEstate = auction.listing_type === "estate";
  const scheduled = auction.status === "scheduled";

  return (
    <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: index * 0.05 }}>
      <Link
        to={`/auction/${auction.auction_id}`}
        data-testid={`auction-card-${auction.auction_id}`}
        className={`group relative flex flex-col justify-between rounded-xl bg-[#121721] border overflow-hidden hover:-translate-y-1 hover:shadow-[0_0_25px_rgba(245,158,11,0.15)] transition-all duration-300 h-full ${auction.featured ? "border-amber-500/60 ring-1 ring-amber-500/40" : "border-white/10 hover:border-amber-500/40"}`}
      >
        <div className="relative aspect-[16/10] overflow-hidden bg-slate-900">
          {auction.image_url ? (
            <img src={asset(auction.image_url)} alt={auction.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-700">
              {isEstate ? <Layers className="h-10 w-10" /> : <Package className="h-10 w-10" />}
            </div>
          )}
          {auction.featured && (
            <span data-testid="card-featured" className="absolute bottom-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-amber-500 text-black">
              <Star className="h-3 w-3" /> Featured
            </span>
          )}
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-xs font-semibold uppercase tracking-wider bg-black/60 backdrop-blur text-amber-300 border border-amber-500/30">
            {isEstate && <Layers className="h-3 w-3" />} {catLabel(auction.category)}
          </span>
          <span
            data-testid={`auction-timer-${auction.auction_id}`}
            className={`absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-xs font-mono font-semibold backdrop-blur border ${
              scheduled ? "bg-sky-500/20 text-sky-300 border-sky-500/30" : t.urgent ? "bg-red-500/25 text-red-200 border-red-500/40" : "bg-black/60 text-slate-200 border-white/20"
            }`}
          >
            <Clock className="h-3 w-3" /> {scheduled ? "Upcoming" : t.label}
          </span>
        </div>

        <div className="p-5 flex flex-col gap-3 flex-1">
          <h3 className={`text-lg tracking-tight line-clamp-1 ${auction.bold ? "font-extrabold text-amber-300" : "font-semibold text-white"}`}>{auction.title}</h3>
          {auction.shop_name && (
            <span className="inline-flex items-center gap-1 text-xs text-amber-300/90 -mt-1"><Store className="h-3 w-3" /> {auction.shop_name}</span>
          )}
          <p className="text-sm text-slate-400 line-clamp-2 flex-1">{auction.description}</p>

          <div className="flex items-end justify-between pt-1">
            <div>
              <div className="text-[11px] text-slate-500 uppercase tracking-wider">{auction.bid_count > 0 ? "Current bid" : "Starting bid"}</div>
              <div className="text-xl font-mono font-bold text-amber-400">{money(auction.current_price)}</div>
              {auction.has_reserve && (
                <div className={`text-[10px] mt-0.5 ${auction.reserve_met ? "text-green-400" : "text-slate-500"}`}>{auction.reserve_met ? "Reserve met" : "Reserve not met"}</div>
              )}
            </div>
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-300">
              <Gavel className="h-3.5 w-3.5 text-amber-400" /> {auction.bid_count} bid{auction.bid_count === 1 ? "" : "s"}
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};
